import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { Calendar, ChartNoAxesCombined, ClipboardPlus, FilePen, QrCode, Wrench } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';

const getGridCols = (count: number) => {
    switch (count) {
        case 1: return 'grid-cols-1 md:grid-cols-1 lg:grid-cols-1';
        case 2: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2';
        case 3: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
        case 4: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
        case 5: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5';
        default: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'; // fallback
    }
};


const QuickActions = () => {
    const navigate = useNavigate();
    const {
        featureToggles,
        getMoldOrderMainTypeLabel,
    } = useAppState();
    const isDevStage = (process.env.VITE_STAGE || '').toLowerCase() === 'dev';

    const maintenanceLabel = getMoldOrderMainTypeLabel('maintenance');
    const repairLabel = getMoldOrderMainTypeLabel('repair');

    const quickActions = [
        {
            title: `สร้างใบงาน${maintenanceLabel}`,
            description: `สร้างใบงาน${maintenanceLabel}สำหรับแม่พิมพ์`,
            icon: ClipboardPlus,
            iconColorClass: 'text-yellow-500 group-hover:text-yellow-600',
            color: 'bg-yellow-500',
            textColor: 'group-hover:text-white',
            borderColor: 'hover:border-yellow-500',
            onClick: () => navigate(`${ROUTES.CREATE_MOLD_ORDERS}?type=maintenance`),
        },
        {
            title: `สร้างใบงาน${repairLabel}`,
            description: `เริ่มต้นสร้างใบงาน${repairLabel}สำหรับแม่พิมพ์`,
            icon: Wrench,
            iconColorClass: 'text-orange-500 group-hover:text-orange-600',
            color: 'bg-orange-500',
            textColor: 'group-hover:text-white',
            borderColor: 'hover:border-orange-500',
            onClick: () => navigate(`${ROUTES.CREATE_MOLD_ORDERS}?type=repair`),
            isEnable: featureToggles.enableRepairMoldOrder,
        },
        {
            title: 'วิเคราะห์ข้อมูล',
            description: 'ดูสถิติและวิเคราะห์ข้อมูลแม่พิมพ์',
            icon: ChartNoAxesCombined,
            iconColorClass: 'text-teal-500 group-hover:text-teal-600',
            color: 'bg-teal-500',
            textColor: 'group-hover:text-white',
            borderColor: 'hover:border-teal-500',
            onClick: () => navigate(ROUTES.REPORT),
        },
        {
            title: 'สแกน QR Code',
            description: 'สแกนรหัส QR เพื่อค้นหาแม่พิมพ์อย่างรวดเร็ว',
            icon: QrCode,
            iconColorClass: 'text-gray-700 group-hover:text-gray-800',
            color: 'bg-gray-700',
            textColor: 'group-hover:text-white',
            borderColor: 'hover:border-gray-700',
            onClick: () => navigate(ROUTES.DASHBOARD),
        },
        {
            title: 'บอร์ดสถานะใบงาน',
            description: 'ดูความคืบหน้าและจัดการใบงานทั้งหมด',
            icon: Calendar,
            iconColorClass: 'text-blue-500 group-hover:text-blue-600',
            color: 'bg-blue-500',
            textColor: 'group-hover:text-white',
            borderColor: 'hover:border-blue-500',
            onClick: () => navigate(ROUTES.DASHBOARD),
        },
        {
            title: 'Form Editor',
            description: 'เปิดเครื่องมือแก้ไขและพรีวิวแบบฟอร์มสำหรับงานพัฒนา',
            icon: FilePen,
            iconColorClass: 'text-violet-500 group-hover:text-violet-600',
            color: 'bg-violet-500',
            textColor: 'group-hover:text-white',
            borderColor: 'hover:border-violet-500',
            onClick: () => navigate(ROUTES.FORM_JSON_EDITOR),
            isEnable: isDevStage,
        },
    ].filter(action => action.isEnable !== false);

    return (
        <>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-6">การดำเนินการด่วน</h2>
            <div className={`grid ${getGridCols(quickActions.length)} gap-3`}>
                {quickActions.map((action, index) => (
                    <motion.button
                        key={index}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={action.onClick}
                        className={`group py-6 px-6 md:px-4 bg-white shadow-sm rounded-xl border-2 border-gray-100 ${action.borderColor} hover:bg-gradient-to-br from-white to-gray-50 transition-all duration-200 hover:shadow-lg text-center flex flex-col items-start justify-start`}
                    >
                        <div className="w-12 h-12 flex items-center justify-center mb-4 transition-all group-hover:scale-110">
                            <action.icon className={`w-9 h-9 transition-colors ${action.iconColorClass || ''}`} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{action.title}</h3>
                        <p className="text-left text-sm text-gray-600">{action.description}</p>
                    </motion.button>
                ))}
            </div>
        </>
    );
}

export default QuickActions;
